import React, { createContext, useContext, useState, useEffect } from 'react';
import { message } from 'antd';
import api from '../services/api';

const WishlistContext = createContext();

export const useWishlist = () => {
    const context = useContext(WishlistContext);
    if (!context) {
        throw new Error('useWishlist must be used within a WishlistProvider');
    }
    return context;
};

export const WishlistProvider = ({ children }) => {
    const [wishlistItems, setWishlistItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [wishlistLoading, setWishlistLoading] = useState({});

    // Load wishlist from localStorage on mount
    useEffect(() => {
        const savedWishlist = localStorage.getItem('wishlist');
        if (savedWishlist) {
            try {
                setWishlistItems(JSON.parse(savedWishlist));
            } catch (error) {
                console.error('Error loading wishlist from localStorage:', error);
                localStorage.removeItem('wishlist');
            }
        }
    }, []);

    // Save wishlist to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('wishlist', JSON.stringify(wishlistItems));
    }, [wishlistItems]);

    const addToWishlist = async (item, type = 'vendor') => {
        const itemId = item.id;
        const itemKey = `${type}_${itemId}`;
        
        // Check if already in wishlist
        if (wishlistItems.some(wishlistItem => wishlistItem.id === itemId && wishlistItem.type === type)) {
            message.info(`${type === 'vendor' ? 'Vendor' : 'Dish'} already in wishlist`);
            return false;
        }

        setWishlistLoading(prev => ({ ...prev, [itemKey]: true }));

        try {
            // Add to backend if user is authenticated
            const { user } = JSON.parse(localStorage.getItem('user') || '{}');
            if (user?.id) {
                await api.post('/wishlist', {
                    item_id: itemId,
                    item_type: type,
                    item_data: item
                });
            }

            // Add to local state
            const wishlistItem = {
                id: itemId,
                type,
                data: item,
                addedAt: new Date().toISOString()
            };

            setWishlistItems(prev => [...prev, wishlistItem]);
            message.success(`${type === 'vendor' ? 'Vendor' : 'Dish'} added to wishlist`);
            return true;
        } catch (error) {
            console.error('Error adding to wishlist:', error);
            message.error('Failed to add to wishlist');
            return false;
        } finally {
            setWishlistLoading(prev => ({ ...prev, [itemKey]: false }));
        }
    };

    const removeFromWishlist = async (itemId, type = 'vendor') => {
        const itemKey = `${type}_${itemId}`;
        
        setWishlistLoading(prev => ({ ...prev, [itemKey]: true }));

        try {
            // Remove from backend if user is authenticated
            const { user } = JSON.parse(localStorage.getItem('user') || '{}');
            if (user?.id) {
                await api.delete(`/wishlist/${itemId}`, {
                    data: { item_type: type }
                });
            }

            // Remove from local state
            setWishlistItems(prev => 
                prev.filter(item => !(item.id === itemId && item.type === type))
            );
            message.success(`${type === 'vendor' ? 'Vendor' : 'Dish'} removed from wishlist`);
            return true;
        } catch (error) {
            console.error('Error removing from wishlist:', error);
            message.error('Failed to remove from wishlist');
            return false;
        } finally {
            setWishlistLoading(prev => ({ ...prev, [itemKey]: false }));
        }
    };

    const toggleWishlist = async (item, type = 'vendor') => {
        const itemId = item.id;
        const isInWishlist = wishlistItems.some(
            wishlistItem => wishlistItem.id === itemId && wishlistItem.type === type
        );

        if (isInWishlist) {
            return await removeFromWishlist(itemId, type);
        } else {
            return await addToWishlist(item, type);
        }
    };

    const isInWishlist = (itemId, type = 'vendor') => {
        return wishlistItems.some(
            item => item.id === itemId && item.type === type
        );
    };

    const getWishlistByType = (type) => {
        return wishlistItems.filter(item => item.type === type);
    };

    const clearWishlist = async () => {
        try {
            // Clear from backend if user is authenticated
            const { user } = JSON.parse(localStorage.getItem('user') || '{}');
            if (user?.id) {
                await api.delete('/wishlist/clear');
            }

            setWishlistItems([]);
            message.success('Wishlist cleared');
        } catch (error) {
            console.error('Error clearing wishlist:', error);
            message.error('Failed to clear wishlist');
        }
    };

    const syncWishlist = async () => {
        const { user } = JSON.parse(localStorage.getItem('user') || '{}');
        if (!user?.id) return;

        setLoading(true);
        try {
            const response = await api.get('/wishlist');
            if (response.data.success) {
                setWishlistItems(response.data.data || []);
            }
        } catch (error) {
            console.error('Error syncing wishlist:', error);
        } finally {
            setLoading(false);
        }
    };

    const getWishlistCount = () => wishlistItems.length;

    const getWishlistCountByType = (type) => {
        return wishlistItems.filter(item => item.type === type).length;
    };

    const value = {
        wishlistItems,
        loading,
        wishlistLoading,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        isInWishlist,
        getWishlistByType,
        clearWishlist,
        syncWishlist,
        getWishlistCount,
        getWishlistCountByType,
    };

    return (
        <WishlistContext.Provider value={value}>
            {children}
        </WishlistContext.Provider>
    );
};

export default WishlistProvider;
